// Ícone e tom dos KPIs saem de `VOCABULARIO_KPI` (`src/auditoria/components/ds`).

import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { DsTone } from 'src/auditoria/components/ds';

import dayjs from 'dayjs';
import useSWR from 'swr';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  DsKpiCard,
  GradeCanvas,
  DsPageHeader,
  DsIconButton,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { isCancelada, formatCurrency, getSituacaoLabel, SITUACAO_EM_ANALISE } from '../Nfse/types';

// ----------------------------------------------------------------------
// Ponte de tipo: `TxDataGridProps.sx` é `SxProps` do @mui/system (tema `{}`),
// mais estreito que o `SxProps<Theme>` que o kit publica. Os valores são os
// mesmos — só o parâmetro de tema difere.
// ----------------------------------------------------------------------

const { sx: SX_GRADE, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE_COMPAT = SX_GRADE as SxProps;

// ----------------------------------------------------------------------
// Contribuinte Externo → Consultar Serviços Prestados. Notas emitidas PELO
// contribuinte PJ (ele é o prestador). Backend: GET /portal-api/externo/prestados
// (nfeemitidaset WHERE cpfcnpjemissor = login; mostra o TOMADOR).
//
// Composição do artboard `portal-externo-prestados.dc.html`: cabeçalho com CTA
// `.btnpri` → 4 KPIs com o rótulo ACIMA do número (`order="title-first"` é
// prescrição desta tela, §5.2) e border-left de 3px → casulo de grade com
// `.thead`/`.trow` e o `.iconbtn` de abrir no fim da linha. Sem `.pager`.
// ----------------------------------------------------------------------

interface PrestadoItem {
  id: number;
  numero: string | null;
  data_emissao: string;
  valor: number;
  situacao: number;
  tomador: { nome: string | null; cnpj: string | null } | null;
}
interface PrestadosResponse {
  notas: PrestadoItem[];
}

function formatCompetencia(value: string | null | undefined): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MM/YYYY') : '—';
}

/** `getSituacaoColor` devolve `LabelColor`, mais largo que a união do kit. */
function tomDaSituacao(situacao: number): DsTone {
  if (isCancelada(situacao)) return 'error';
  if (situacao === SITUACAO_EM_ANALISE) return 'warning';
  return 'success';
}

export default function ExternoPrestadosListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<PrestadosResponse>('/portal-api/externo/prestados', fetcher);

  const notas = useMemo(() => data?.notas ?? [], [data]);

  // KPIs derivados do que a lista devolve (número, valor e situação). O aceite do
  // tomador e o ISS próprio não vêm neste endpoint — ver recibo do lote.
  const kpis = useMemo(() => {
    const soma = (itens: PrestadoItem[]) => itens.reduce((s, n) => s + Number(n.valor ?? 0), 0);
    const emitidas = notas.filter(
      (n) => !isCancelada(n.situacao) && n.situacao !== SITUACAO_EM_ANALISE
    );
    const emAnalise = notas.filter((n) => n.situacao === SITUACAO_EM_ANALISE);
    const canceladas = notas.filter((n) => isCancelada(n.situacao));
    return {
      total: { qtd: notas.length, valor: soma(notas) },
      emitidas: { qtd: emitidas.length, valor: soma(emitidas) },
      emAnalise: { qtd: emAnalise.length, valor: soma(emAnalise) },
      canceladas: { qtd: canceladas.length, valor: soma(canceladas) },
    };
  }, [notas]);

  const columns: GridColDef<PrestadoItem>[] = [
    {
      field: 'numero',
      headerName: 'Número',
      width: 110,
      valueGetter: (_v, r) => r.numero ?? '—',
      renderCell: (params) => <DsNum mono>{params.row.numero ?? '—'}</DsNum>,
    },
    {
      field: 'tomador_nome',
      headerName: 'Tomador',
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, r) => r.tomador?.nome ?? r.tomador?.cnpj ?? '—',
    },
    {
      field: 'data_emissao',
      headerName: 'Competência',
      width: 120,
      valueGetter: (_v, r) => formatCompetencia(r.data_emissao),
      renderCell: (params) => <DsNum mono>{formatCompetencia(params.row.data_emissao)}</DsNum>,
    },
    {
      field: 'valor',
      headerName: 'Valor (R$)',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, r) => Number(r.valor ?? 0),
      renderCell: (params) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(params.row.valor)}
        </DsNum>
      ),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 140,
      renderCell: (params) => (
        <DsStatusChip tone={tomDaSituacao(params.row.situacao)}>
          {getSituacaoLabel(params.row.situacao)}
        </DsStatusChip>
      ),
    },
    {
      field: 'abrir',
      headerName: '',
      width: 56,
      align: 'right',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <DsIconButton
          icon="chevron-dir"
          aria-label={`Abrir a nota ${params.row.numero ?? params.row.id}`}
          onClick={() => navigate(`/externo/prestados/${params.row.id}`)}
        />
      ),
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        title="Serviços Prestados"
        weight={800}
        subtitle="Escrituração de ISS do contribuinte externo · aceite do tomador."
        action={
          <DsButton
            tone="primary"
            size="tall"
            icon="mais"
            onClick={() => navigate('/externo/prestados/novo')}
          >
            Escriturar prestado
          </DsButton>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: '12px',
          mb: '12px',
        }}
      >
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Escrituradas"
          total={<DsNum>{kpis.total.qtd}</DsNum>}
          sub={formatCurrency(kpis.total.valor)}
          conceito="notas"
          borderLeft
        />
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Emitidas"
          total={<DsNum>{kpis.emitidas.qtd}</DsNum>}
          sub={formatCurrency(kpis.emitidas.valor)}
          conceito="documento-regular"
          borderLeft
        />
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Em análise"
          total={<DsNum>{kpis.emAnalise.qtd}</DsNum>}
          sub={formatCurrency(kpis.emAnalise.valor)}
          conceito="aguardando"
          borderLeft
        />
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Canceladas"
          total={<DsNum>{kpis.canceladas.qtd}</DsNum>}
          sub={formatCurrency(kpis.canceladas.valor)}
          conceito="cancelado"
          borderLeft
        />
      </Box>

      <GradeCanvas>
        <TxDataGrid<PrestadoItem>
          rows={notas}
          columns={columns}
          loading={isLoading}
          emptyLabel="Nenhuma nota escriturada"
          autoHeight
          hideFooter
          {...PROPS_GRADE}
          sx={SX_GRADE_COMPAT}
        />
      </GradeCanvas>
    </DashboardContent>
  );
}
